import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Wallet, Plus, Minus, Search, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errorHelpers';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminWalletManagement = () => {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeductModal, setShowDeductModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);

  // Add credits form
  const [addForm, setAddForm] = useState({ credits: 10, reason: '', note: '' });
  const [deductForm, setDeductForm] = useState({ credits: 5, reason: '', note: '' });

  useEffect(() => {
    fetchWallets();
  }, []);

  const fetchWallets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/wallet/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setWallets(response.data.wallets);
    } catch (error) {
      console.error('Error fetching wallets:', error);
      toast.error(getErrorMessage(error, 'Failed to load wallets'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddCredits = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/admin/wallet/add-credits`,
        {
          tenant_id: selectedTenant.tenant_id,
          credits: parseFloat(addForm.credits),
          reason: addForm.reason,
          note: addForm.note
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success(`Successfully added ${addForm.credits} credits to ${selectedTenant.tenant_name}`);
      setShowAddModal(false);
      setAddForm({ credits: 10, reason: '', note: '' });
      fetchWallets();
    } catch (error) {
      console.error('Error adding credits:', error);
      toast.error(getErrorMessage(error, 'Failed to add credits'));
    }
  };

  const handleDeductCredits = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/admin/wallet/deduct-credits`,
        {
          tenant_id: selectedTenant.tenant_id,
          credits: parseFloat(deductForm.credits),
          reason: deductForm.reason,
          note: deductForm.note
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success(`Successfully deducted ${deductForm.credits} credits from ${selectedTenant.tenant_name}`);
      setShowDeductModal(false);
      setDeductForm({ credits: 5, reason: '', note: '' });
      fetchWallets();
    } catch (error) {
      console.error('Error deducting credits:', error);
      toast.error(getErrorMessage(error, 'Failed to deduct credits'));
    }
  };

  const filteredWallets = wallets.filter(w =>
    w.tenant_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.tenant_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="h-96 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <Wallet className="w-8 h-8" />
            Tenant Wallet Management
          </h1>
          <Button
            onClick={() => window.location.href = '/admin/wallet/pricing'}
            variant="outline"
          >
            <Settings className="w-4 h-4 mr-2" />
            Pricing Config
          </Button>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Search className="w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search tenants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Wallets Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Tenant Wallets ({filteredWallets.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Credits</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Equivalent Value</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Spent</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Operations</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredWallets.map((wallet) => (
                    <tr key={wallet.tenant_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div>
                          <div className="font-medium text-gray-800">{wallet.tenant_name}</div>
                          <div className="text-xs text-gray-500">{wallet.tenant_id}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">
                        {wallet.balance.toFixed(2)} Credits
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">
                        Equivalent Value: ₹{(wallet.reference_inr_value || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">
                        {wallet.total_spent.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">
                        {wallet.total_operations}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          wallet.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {wallet.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedTenant(wallet);
                              setShowAddModal(true);
                            }}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedTenant(wallet);
                              setShowDeductModal(true);
                            }}
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Add Credits Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle>Add Credits to {selectedTenant?.tenant_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <div className="text-sm text-gray-600 mb-1">Current Balance</div>
                <div className="text-2xl font-bold">{selectedTenant?.balance.toFixed(2)} credits</div>
              </div>
              <div>
                <Label>Credits to Add</Label>
                <Input
                  type="number"
                  value={addForm.credits}
                  onChange={(e) => setAddForm({...addForm, credits: e.target.value})}
                  min="1"
                />
              </div>
              <div>
                <Label>Reason *</Label>
                <Input
                  value={addForm.reason}
                  onChange={(e) => setAddForm({...addForm, reason: e.target.value})}
                  placeholder="e.g., Promotional credit"
                />
              </div>
              <div>
                <Label>Note (optional)</Label>
                <Textarea
                  value={addForm.note}
                  onChange={(e) => setAddForm({...addForm, note: e.target.value})}
                  placeholder="Additional details..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleAddCredits}
                disabled={!addForm.reason || addForm.credits < 1}
                className="bg-green-600 hover:bg-green-700"
              >
                Add {addForm.credits} Credits
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Deduct Credits Modal */}
        <Dialog open={showDeductModal} onOpenChange={setShowDeductModal}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle>Deduct Credits from {selectedTenant?.tenant_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <div className="text-sm text-gray-600 mb-1">Current Balance</div>
                <div className="text-2xl font-bold">{selectedTenant?.balance.toFixed(2)} credits</div>
              </div>
              <div>
                <Label>Credits to Deduct</Label>
                <Input
                  type="number"
                  value={deductForm.credits}
                  onChange={(e) => setDeductForm({...deductForm, credits: e.target.value})}
                  min="1"
                  max={selectedTenant?.balance || 0}
                />
              </div>
              <div>
                <Label>Reason *</Label>
                <Input
                  value={deductForm.reason}
                  onChange={(e) => setDeductForm({...deductForm, reason: e.target.value})}
                  placeholder="e.g., Adjustment for failed operation"
                />
              </div>
              <div>
                <Label>Note (optional)</Label>
                <Textarea
                  value={deductForm.note}
                  onChange={(e) => setDeductForm({...deductForm, note: e.target.value})}
                  placeholder="Additional details..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeductModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleDeductCredits}
                disabled={!deductForm.reason || deductForm.credits < 1}
                className="bg-red-600 hover:bg-red-700"
              >
                Deduct {deductForm.credits} Credits
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminWalletManagement;
